import { useLingui } from "@lingui/react/macro";
import type { SlashActionId } from "@rakazo/core";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@rakazo/ui-web";
import { MoreHorizontal, Zap } from "lucide-react";
import type { ReactNode } from "react";

/**
 * Kommerce: quiet text-button look shared by every control below the composer.
 * Ghost by default so the message field stays the visual focus.
 */
export const toolbarButtonClass =
  "inline-flex h-8 shrink-0 items-center gap-1 rounded-lg px-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";

export const toolbarIconButtonClass =
  "inline-flex size-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";

/**
 * Kommerce: the row under the message field. Leading controls (attach, voice, quick actions)
 * always show; trailing controls (usage, tools, model, thinking) sit inline from `md`
 * and collapse into one overflow menu on narrow screens.
 */
export function ComposerToolbar({ leading, trailing }: { leading: ReactNode; trailing?: ReactNode }) {
  const { t } = useLingui();
  return (
    <div data-testid="composer-toolbar" className="mt-1.5 flex min-w-0 items-center justify-between gap-2 px-1">
      <div className="flex min-w-0 items-center gap-0.5">{leading}</div>
      {trailing ? (
        <>
          <div className="hidden min-w-0 items-center gap-0.5 md:flex">{trailing}</div>
          <Popover>
            <PopoverTrigger aria-label={t`Chat options`} className={`${toolbarIconButtonClass} md:hidden`}>
              <MoreHorizontal size={16} strokeWidth={2} />
            </PopoverTrigger>
            <PopoverContent align="end" side="top" className="w-64 gap-1 p-2">
              <div className="flex flex-col items-stretch gap-1 [&>*]:justify-between">{trailing}</div>
            </PopoverContent>
          </Popover>
        </>
      ) : null}
    </div>
  );
}

/** Kommerce: one-tap prompts and shortcuts, sent as a normal message to the active bot. */
export function ComposerQuickActions({
  disabled,
  onRun,
  onSlashAction,
}: {
  disabled?: boolean;
  onRun: (prompt: string) => void;
  onSlashAction?: (action: SlashActionId) => void;
}) {
  const { t } = useLingui();
  const actions = [
    {
      label: t`Summarize this chat`,
      prompt: t`Summarize this conversation so far: decisions made, open questions, and next steps.`,
    },
    {
      label: t`Save key points to shared memory`,
      prompt: t`Save the durable decisions, facts and preferences from this conversation to shared memory so every bot in this space knows them. Then list exactly what you saved.`,
    },
    {
      label: t`Turn this into a plan`,
      prompt: t`Turn what we discussed into a short, numbered plan with an owner and the first concrete step for each item.`,
    },
  ];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger aria-label={t`Quick actions`} disabled={disabled} className={toolbarIconButtonClass}>
        <Zap size={15} strokeWidth={2} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-64">
        {actions.map((action) => (
          <DropdownMenuItem key={action.label} onClick={() => onRun(action.prompt)}>
            {action.label}
          </DropdownMenuItem>
        ))}
        {onSlashAction ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onSlashAction("chat-settings")}>{t`Bot settings`}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSlashAction("settings-usage")}>{t`Usage`}</DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
